import { useEffect, useState } from 'react';

/**
 * Tailwind v4 default breakpoints (px). Mirrors phase-0-spec.md §3.
 * Mobile-first: the value is the *minimum* viewport width for that name.
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** Ordered from smallest to largest. */
const BREAKPOINT_ORDER: readonly Breakpoint[] = ['sm', 'md', 'lg', 'xl', '2xl'];

/**
 * The active named breakpoint for a given viewport width, or `null` for
 * anything below `sm` (phone). This matches Tailwind's mobile-first model:
 * a width of 800 returns `'md'` because it's ≥768 and <1024.
 */
function bpForWidth(width: number): Breakpoint | null {
  let current: Breakpoint | null = null;
  for (const bp of BREAKPOINT_ORDER) {
    if (width >= BREAKPOINTS[bp]) current = bp;
  }
  return current;
}

/**
 * Returns the current named breakpoint for `window.innerWidth`, or `null`
 * for viewports narrower than `sm` (640px). Re-renders on viewport resize.
 *
 * Use this for shell/page-level decisions (e.g. "show bottom-nav below md").
 * For component-internal responsiveness, prefer `useContainerQuery` so
 * components react to their *container* width rather than the viewport.
 *
 * SSR-safe — returns `null` until the first effect runs in the browser.
 */
export function useBreakpoint(): Breakpoint | null {
  const [bp, setBp] = useState<Breakpoint | null>(() =>
    typeof window === 'undefined' ? null : bpForWidth(window.innerWidth),
  );

  useEffect(() => {
    const handler = () => {
      setBp(bpForWidth(window.innerWidth));
    };
    handler();
    window.addEventListener('resize', handler, { passive: true });
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  return bp;
}

/**
 * True when the viewport is at least the given breakpoint width.
 * `useIsAtLeast('md')` returns true at 768px+.
 */
export function useIsAtLeast(bp: Breakpoint): boolean {
  const current = useBreakpoint();
  if (current === null) return false;
  return BREAKPOINTS[current] >= BREAKPOINTS[bp];
}
